import { differenceInDays } from "date-fns";

import { ChatbotSessionI } from "@seneca/chatbot-service-types";
import { Box, Divider, Text, useTheme } from "@seneca/senekit";

import { SessionSidebarItem } from "./SessionSidebarItem";

type ChatSessionsGroup = {
  today: ChatbotSessionI[];
  lastSevenDays: ChatbotSessionI[];
  lastThirtyDays: ChatbotSessionI[];
  older: ChatbotSessionI[];
};

export default function Sessions({
  activeSessionId,
  courseId,
  sessions
}: {
  courseId: string;
  activeSessionId: string;
  sessions: ChatSessionsGroup;
}) {
  const { today, lastSevenDays, lastThirtyDays, older } = sessions;
  const themes = useTheme();

  return (
    <Box backgroundColor={themes["foreground"]} borderRadius="small">
      {today.length > 0 && (
        <>
          <GroupHeading text="Today" />
          <SessionSidebarItemList
            items={today}
            activeSessionId={activeSessionId}
            courseId={courseId}
          />
        </>
      )}
      {lastSevenDays.length > 0 && (
        <>
          {today.length > 0 && <Divider />}
          <GroupHeading text="Last 7 days" />
          <SessionSidebarItemList
            items={lastSevenDays}
            activeSessionId={activeSessionId}
            courseId={courseId}
          />
        </>
      )}
      {lastThirtyDays.length > 0 && (
        <>
          <Divider />
          <GroupHeading text="Last 30 days" />
          <SessionSidebarItemList
            items={lastThirtyDays}
            activeSessionId={activeSessionId}
            courseId={courseId}
          />
        </>
      )}
      {older.length > 0 && (
        <>
          <Divider />
          <GroupHeading text="Older" />
          <SessionSidebarItemList
            items={older}
            activeSessionId={activeSessionId}
            courseId={courseId}
          />
        </>
      )}
    </Box>
  );
}

function GroupHeading({ text }: { text: string }) {
  return (
    <Box padding="12" paddingY="large">
      <Text emphasis="medium" size="xsmall" weight="bold">
        {text}
      </Text>
    </Box>
  );
}

function SessionSidebarItemList({
  items,
  activeSessionId,
  courseId
}: {
  items: ChatbotSessionI[];
  activeSessionId?: string;
  courseId: string;
}) {
  return (
    <>
      {items.map(chatSession => (
        <SessionSidebarItem
          key={chatSession.sessionId}
          title={chatSession.title}
          isActive={chatSession.sessionId === activeSessionId}
          courseId={courseId}
          sessionId={chatSession.sessionId}
        />
      ))}
    </>
  );
}

export function groupByPeriod(
  chatSessions: ChatbotSessionI[]
): ChatSessionsGroup {
  const group: ChatSessionsGroup = {
    today: [],
    lastSevenDays: [],
    lastThirtyDays: [],
    older: []
  };

  chatSessions.forEach(chatSession => {
    const today = new Date();
    const timestamp = new Date(chatSession.timestamp);
    const diffDays = differenceInDays(today, timestamp);

    if (diffDays === 0) {
      group["today"].push(chatSession);
    } else if (diffDays <= 7) {
      group["lastSevenDays"].push(chatSession);
    } else if (diffDays <= 30) {
      group["lastThirtyDays"].push(chatSession);
    } else {
      group["older"].push(chatSession);
    }
  });

  return group;
}
